import { ComponentChild } from 'preact'
import { PureComponent } from 'preact-compat'
import {
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_CONVERSATION,
  CONTENT_TYPE_IDEA,
  CONTENT_TYPE_QUESTION,
  ContentType
} from '@insided/shared/utils/contentType'
import ChangeAuthorModal from './ChangeAuthorModal'
import { ToggleTrashModal, ToggleTrash } from './ToggleTrash'
import { Author } from '../interfaces'
import ConvertTopicModal from './ConvertTopicModal'

/* eslint-disable import/no-internal-modules */
import { ModulesContext } from '@insided/deprecated-utils/globals'
import DropdownControl from '@insided/deprecated-preact-components/dropdown/DropdownControl'
import Button from '@insided/control/uikit/Button'
import { ParentCategory } from '@insided/deprecated-preact-components/category-selector/CategorySelector'
import { ConfigContext } from '../utils'

interface MoreOptionsProps {
  topicId: number
  publicId: number
  contentType: ContentType
  trashed: boolean
  spam: boolean
  handleToggleSpamModal: () => void
  onTrash: (trashed: boolean) => void
  onShowAlert: (type: 'success' | 'error', message?: string) => void
  categories: ParentCategory[]
  setMessage: (message?: ComponentChild) => void
  hasPinnedReply: boolean
  onChangeAuthor: (author: Author) => void
}

interface MoreOptionsState {
  showChangeAuthorModal: boolean
  showConversionModal: boolean
  showToggleTrashModal: boolean
  convertToContentType: ContentType | null
}

class MoreOptions extends PureComponent<MoreOptionsProps, MoreOptionsState> {
  constructor () {
    super()
    this.state = {
      showChangeAuthorModal: false,
      showConversionModal: false,
      showToggleTrashModal: false,
      convertToContentType: null
    }

    this.showConversionModal = this.showConversionModal.bind(this)
    this.hideConversionModal = this.hideConversionModal.bind(this)
  }

  showChangeAuthorModal (show: boolean) {
    this.setState({ showChangeAuthorModal: show })
  }

  showToggleTrashModal (show: boolean) {
    this.setState({ showToggleTrashModal: show })
  }

  showConversionModal (contentType: ContentType) {
    const { hasPinnedReply, onShowAlert } = this.props
    if (hasPinnedReply) {
      onShowAlert(
        'error',
        'It\'s not possible to convert a topic with a pinned reply. Please first unpin the reply and try again.'
      )
      return
    }
    this.setState({
      showConversionModal: true,
      convertToContentType: contentType
    })
  }

  hideConversionModal () {
    this.setState({
      showConversionModal: false,
      convertToContentType: null
    })
  }

  render () {
    const { topicId, publicId, contentType, trashed, onTrash, spam, handleToggleSpamModal, onShowAlert, categories, setMessage, onChangeAuthor } = this.props
    const { showConversionModal, showChangeAuthorModal, showToggleTrashModal, convertToContentType } = this.state

    const changeAuthor = {
      title: 'Change author',
      onClick: () => this.showChangeAuthorModal(true),
      variant: 'primary'
    }

    const convertToQuestion = {
      title: 'Convert to question',
      onClick: () => this.showConversionModal(CONTENT_TYPE_QUESTION),
      variant: 'primary'
    }

    const convertToConversation = {
      title: 'Convert to conversation',
      onClick: () => this.showConversionModal(CONTENT_TYPE_CONVERSATION),
      variant: 'primary'
    }

    const convertToArticle = {
      title: 'Convert to article',
      onClick: () => this.showConversionModal(CONTENT_TYPE_ARTICLE),
      variant: 'primary'
    }

    const convertToIdea = {
      title: 'Convert to idea',
      onClick: () => this.showConversionModal(CONTENT_TYPE_IDEA),
      variant: 'primary'
    }

    const toggleSpam = {
      title: spam ? 'Mark as not spam' : 'Mark as spam',
      onClick: handleToggleSpamModal,
      variant: spam ? 'primary' : 'danger'
    }

    const toggleTrash = {
      title: trashed ? 'Restore' : 'Trash',
      onClick: trashed ? async () => await ToggleTrash(this.props) : () => this.showToggleTrashModal(true),
      variant: trashed ? 'primary' : 'danger'
    }

    return (
      <div>
        <ModulesContext.Consumer>
          {(modules) => {
            return (
              <ConfigContext.Consumer>
                {({ contentTypeConfig }) => {
                  return (
                    <DropdownControl
                      align='adjust'
                      triggerClass='dropdown-wrapper'
                      trigger={
                        <Button icon='arrowDropdown' title='More options' className='qa-topic-moderation-more-options' />
                      }
                      items={[
                        ...modules.includes('changeAuthorOfPublishedContent') && contentTypeConfig.changeAuthor ? [changeAuthor] : [],
                        ...contentTypeConfig.convertToQuestion ? [convertToQuestion] : [],
                        ...contentTypeConfig.convertToConversation ? [convertToConversation] : [],
                        ...contentTypeConfig.convertToArticle ? [convertToArticle] : [],
                        ...modules.includes('ideationV2') && contentTypeConfig.convertToIdea ? [convertToIdea] : [],
                        ...contentTypeConfig.spam && !trashed ? [toggleSpam] : [],
                        ...!spam ? [toggleTrash] : []
                      ] as any}
                    />
                  )
                }}
              </ConfigContext.Consumer>
            )
          }}
        </ModulesContext.Consumer>
        {showChangeAuthorModal &&
          <ChangeAuthorModal
            contentType={contentType}
            topicId={topicId}
            onChangeAuthor={onChangeAuthor}
            onClose={() => this.showChangeAuthorModal(false)}
            onShowAlert={onShowAlert}
          />}
        {showConversionModal && convertToContentType &&
          <ConvertTopicModal
            topicId={topicId}
            publicId={publicId}
            fromContentType={contentType}
            toContentType={convertToContentType}
            categories={categories}
            onShowAlert={onShowAlert}
            hideModal={this.hideConversionModal}
            setMessage={setMessage}
          />}
        {showToggleTrashModal &&
          <ToggleTrashModal
            contentType={contentType}
            topicId={topicId}
            trashed={trashed}
            onTrash={onTrash}
            onClose={() => this.showToggleTrashModal(false)}
            onShowAlert={onShowAlert}
          />}
      </div>
    )
  }
}

export default MoreOptions
